// src/components/products/forms/MarkupsEditor.tsx
import React from "react";
import { FiPlus, FiTrash2, FiPercent } from "react-icons/fi";

import type {
  CustomerType,
  TravelScope,
  TaxMethod,
  ProductMarkup,
  ProductMarkupPayload,
} from "@/api/features/products/types";

/* ======================================================================
 * Shared markup editor used by Create + Edit product forms.
 *
 * Each row is one scoped markup: (customerType, travelScope) → rate/method.
 * `scope === ""` represents "All scopes" (persisted as travelScope = null).
 * ====================================================================== */

export type MarkupRow = {
  id?: number;
  customerType: CustomerType;
  scope: "" | TravelScope; // "" => all scopes
  rate: string;
  method: TaxMethod;
  /** Currency a FLAT rate is stated in. Irrelevant to PERCENT. */
  currency: string;
};

/**
 * Currencies a fixed markup can be stated in. A fixed fee is a bare amount, so
 * it MUST say which currency it is in: a 100 configured against a fare the
 * supplier quoted in USD is otherwise charged as $100.
 */
export const MARKUP_CURRENCY_OPTIONS = [
  "INR",
  "AED",
  "USD",
  "CAD",
  "EUR",
  "GBP",
  "SGD",
  "MYR",
  "NZD",
  "AUD",
] as const;

export const DEFAULT_MARKUP_CURRENCY = "INR";

export const CUSTOMER_TYPE_OPTIONS: { value: CustomerType; label: string }[] = [
  { value: "CUSTOMER", label: "Customer (B2C)" },
  { value: "CORPORATE", label: "Corporate (B2B)" },
  { value: "AFFILIATE", label: "Affiliate / Agent" },
];

export const TRAVEL_SCOPE_OPTIONS: { value: "" | TravelScope; label: string }[] =
  [
    { value: "", label: "All scopes" },
    { value: "DOMESTIC", label: "Domestic" },
    { value: "INTERNATIONAL", label: "International" },
  ];

/** Default rows: one "All scopes" markup per customer type. */
export function makeDefaultMarkupRows(): MarkupRow[] {
  return CUSTOMER_TYPE_OPTIONS.map((c) => ({
    customerType: c.value,
    scope: "",
    rate: "",
    method: "PERCENT" as TaxMethod,
    currency: DEFAULT_MARKUP_CURRENCY,
  }));
}

/** Hydrate editor rows from persisted markups (falls back to defaults). */
export function markupRowsFromConfig(
  markups: ProductMarkup[] | undefined | null
): MarkupRow[] {
  if (!markups || markups.length === 0) return makeDefaultMarkupRows();

  return markups.map((m) => ({
    id: m.id,
    customerType: m.customerType,
    scope: (m.travelScope ?? "") as "" | TravelScope,
    rate:
      m.markupRate === null || m.markupRate === undefined
        ? ""
        : String(m.markupRate),
    method: m.markupMethod ?? "PERCENT",
    currency:
      String(m.markupCurrency || DEFAULT_MARKUP_CURRENCY)
        .trim()
        .toUpperCase() || DEFAULT_MARKUP_CURRENCY,
  }));
}

/**
 * Why a row's rate cannot be saved, or null when it can. Mirrors the API's
 * rules: a number, not negative, and at most 100 for a percentage. Blank is
 * allowed and means 0 (no markup).
 */
export function markupRowError(row: MarkupRow): string | null {
  const trimmed = row.rate.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a number.";
  if (n < 0) return "Rate cannot be negative.";
  if (row.method === "PERCENT" && n > 100) return "A percentage cannot exceed 100.";
  return null;
}

/**
 * Convert editor rows into the API payload.
 * - Blank rate is treated as 0 (explicit "no markup").
 * - De-duplicates by (customerType, scope) — last row wins.
 */
export function markupRowsToPayload(rows: MarkupRow[]): ProductMarkupPayload[] {
  const seen = new Map<string, ProductMarkupPayload>();

  for (const r of rows) {
    const key = `${r.customerType}::${r.scope || "ALL"}`;
    const rateTrimmed = r.rate.trim();

    const payload: ProductMarkupPayload = {
      customerType: r.customerType,
      markupRate: rateTrimmed === "" ? 0 : Number(rateTrimmed) || 0,
      markupMethod: r.method,
      markupCurrency:
        String(r.currency || DEFAULT_MARKUP_CURRENCY).trim().toUpperCase() ||
        DEFAULT_MARKUP_CURRENCY,
      travelScope: r.scope === "" ? null : r.scope,
    };
    if (typeof r.id === "number") payload.id = r.id;

    seen.set(key, payload);
  }

  return Array.from(seen.values());
}

/* ====================================================================== */

type Props = {
  value: MarkupRow[];
  onChange: (next: MarkupRow[]) => void;
  disabled?: boolean;
  /** Product name shown in the "Fixed" markup hint (e.g. flight, hotel, tour). */
  productName?: string;
};

export const MarkupsEditor: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  productName,
}) => {
  const productLabel = productName?.trim() || "product";
  const addRow = () => {
    onChange([
      ...value,
      {
        customerType: "CUSTOMER",
        scope: "",
        rate: "",
        method: "PERCENT",
        currency: DEFAULT_MARKUP_CURRENCY,
      },
    ]);
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateRow = <K extends keyof MarkupRow>(
    index: number,
    key: K,
    val: MarkupRow[K]
  ) => {
    onChange(value.map((row, i) => (i === index ? { ...row, [key]: val } : row)));
  };

  // Highlight duplicate (customerType, scope) combinations for the user.
  const counts = new Map<string, number>();
  for (const r of value) {
    const key = `${r.customerType}::${r.scope || "ALL"}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Markups</h3>
          <p className="text-xs text-gray-500">
            Set a markup per customer type. Add a Domestic / International row
            when a product (e.g. flights) needs different markups by travel
            scope. &ldquo;All scopes&rdquo; is the fallback when no specific
            scope matches.
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          disabled={disabled}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <FiPlus className="h-3 w-3" />
          Add markup
        </button>
      </div>

      <div className="space-y-3">
        {value.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
            No markups configured. Add at least one per customer type.
          </div>
        )}

        {value.map((row, index) => {
          const key = `${row.customerType}::${row.scope || "ALL"}`;
          const isDup = (counts.get(key) ?? 0) > 1;

          return (
            <div
              key={row.id ?? `markup-new-${index}`}
              className={`rounded-lg border bg-white p-3 ${
                isDup ? "border-amber-300" : "border-gray-200"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                  {/* Customer type */}
                  <label className="block">
                    <div className="mb-1 text-xs font-medium text-gray-700">
                      Customer type
                    </div>
                    <select
                      value={row.customerType}
                      onChange={(e) =>
                        updateRow(
                          index,
                          "customerType",
                          e.target.value as CustomerType
                        )
                      }
                      disabled={disabled}
                      className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                    >
                      {CUSTOMER_TYPE_OPTIONS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Travel scope */}
                  <label className="block">
                    <div className="mb-1 text-xs font-medium text-gray-700">
                      Travel scope
                    </div>
                    <select
                      value={row.scope}
                      onChange={(e) =>
                        updateRow(
                          index,
                          "scope",
                          e.target.value as "" | TravelScope
                        )
                      }
                      disabled={disabled}
                      className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                    >
                      {TRAVEL_SCOPE_OPTIONS.map((s) => (
                        <option key={s.value || "ALL"} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Rate */}
                  <label className="block">
                    <div className="mb-1 text-xs font-medium text-gray-700">
                      Rate
                    </div>
                    <div className="relative">
                      {row.method === "FLAT" && (
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">
                          {row.currency || DEFAULT_MARKUP_CURRENCY}
                        </span>
                      )}
                      <input
                        type="number"
                        min={0}
                        max={row.method === "PERCENT" ? 100 : undefined}
                        step={0.01}
                        aria-invalid={markupRowError(row) ? true : undefined}
                        aria-describedby={markupRowError(row) ? `markup-rate-error-${index}` : undefined}
                        value={row.rate}
                        onChange={(e) =>
                          updateRow(index, "rate", e.target.value)
                        }
                        placeholder="0"
                        disabled={disabled}
                        className={`w-full rounded-lg border border-gray-300 bg-white ${
                          row.method === "FLAT" ? "pl-12" : "pl-3"
                        } ${
                          row.method === "PERCENT" ? "pr-9" : "pr-3"
                        } py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100`}
                      />
                      {row.method === "PERCENT" && (
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                          <FiPercent className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                    {markupRowError(row) && (
                      <p id={`markup-rate-error-${index}`} role="alert" className="mt-1 text-[11px] text-red-600">
                        {markupRowError(row)}
                      </p>
                    )}
                  </label>

                  {/* Method */}
                  <label className="block">
                    <div className="mb-1 text-xs font-medium text-gray-700">
                      Method
                    </div>
                    <select
                      value={row.method}
                      onChange={(e) =>
                        updateRow(index, "method", e.target.value as TaxMethod)
                      }
                      disabled={disabled}
                      className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                    >
                      <option value="PERCENT">Percent</option>
                      <option value="FLAT">Fixed</option>
                    </select>
                    {row.method === "FLAT" && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        Applied per pax per {productLabel}.
                      </p>
                    )}
                  </label>

                  {/* Currency — only a FIXED fee needs one. A percentage is a
                      share of the fare, so it is already in the fare's
                      currency. */}
                  {row.method === "FLAT" && (
                    <label className="block">
                      <div className="mb-1 text-xs font-medium text-gray-700">
                        Fee currency
                      </div>
                      <select
                        value={row.currency || DEFAULT_MARKUP_CURRENCY}
                        onChange={(e) =>
                          updateRow(index, "currency", e.target.value)
                        }
                        disabled={disabled}
                        className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
                      >
                        {MARKUP_CURRENCY_OPTIONS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-[11px] text-gray-500">
                        Converted to the fare&rsquo;s currency at booking time.
                      </p>
                    </label>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  className="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-red-500 hover:bg-red-50 disabled:opacity-60"
                  aria-label="Remove markup"
                >
                  <FiTrash2 />
                </button>
              </div>

              {isDup && (
                <p className="mt-2 text-[11px] text-amber-600">
                  Duplicate customer type + scope. Only the last one will be
                  saved.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
